import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit } from 'lucide-react';
import MensagensTab from '@/components/MensagensTab';

type Cliente = {
  id: string;
  codigo_cliente: string;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  apelido: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  email_nfe: string | null;
  fora_municipio: boolean | null;
  ibge_municipio: string | null;
  limite_credito: number | null;
  observacoes: string | null;
  cnpj_bancario: string | null;
  banco: string | null;
  pix: string | null;
  conta: string | null;
  agencia: string | null;
  matricula_cei_cno: string | null;
  tipo_cliente_id: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  tipo_clientes?: { nome: string } | null;
};

export default function ClienteView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);

  const canRead = hasPermission('clientes', 'clientes', 'read');
  const canWrite = hasPermission('clientes', 'clientes', 'write');

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }

    loadCliente();
  }, [id, canRead]);

  const loadCliente = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('clientes')
        .select(`
          *,
          tipo_clientes (
            nome
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setCliente({ 
        ...data, 
        ativo: data.status === 'ativo',
        tipo_clientes: data.tipo_clientes && !('error' in data.tipo_clientes) ? data.tipo_clientes : null
      });
    } catch (error: any) {
      console.error('Erro ao carregar cliente:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar cliente: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para visualizar clientes.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!cliente) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Cliente não encontrado.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/intranet/clientes-new')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{cliente.nome}</h1>
            <p className="text-muted-foreground">
              Código: {cliente.codigo_cliente}
            </p>
          </div>
        </div>
        {canWrite && (
          <Button onClick={() => navigate(`/intranet/clientes/${cliente.id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>

      <Tabs defaultValue="dados-basicos" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
          <TabsTrigger value="contato">Contato</TabsTrigger>
          <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
          <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="observacoes">Observações</TabsTrigger>
          <TabsTrigger value="mensagens">Mensagens</TabsTrigger>
        </TabsList>

        <TabsContent value="dados-basicos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Código Cliente</label>
                  <p className="text-sm font-mono">{cliente.codigo_cliente}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">
                    <Badge variant={cliente.ativo ? "default" : "secondary"}>
                      {cliente.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Nome/Razão Social</label>
                  <p className="text-sm">{cliente.nome}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Documento (CNPJ/CPF)</label>
                  <p className="text-sm">{cliente.documento || '-'}</p>
                </div>
                {cliente.apelido && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Apelido</label>
                    <p className="text-sm">{cliente.apelido}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Tipo de Cliente</label>
                  <p className="text-sm">{cliente.tipo_clientes?.nome || '-'}</p>
                </div>
                {cliente.matricula_cei_cno && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Matrícula CEI/CNO</label>
                    <p className="text-sm">{cliente.matricula_cei_cno}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contato" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações de Contato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">E-mail</label>
                  <p className="text-sm">{cliente.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Telefone</label>
                  <p className="text-sm">{cliente.telefone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Endereço</label>
                  <p className="text-sm">{cliente.endereco || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Cidade</label>
                  <p className="text-sm">{cliente.cidade || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Estado</label>
                  <p className="text-sm">{cliente.estado || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">CEP</label>
                  <p className="text-sm">{cliente.cep || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fiscal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Fiscais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Inscrição Estadual</label>
                  <p className="text-sm">{cliente.inscricao_estadual || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Inscrição Municipal</label>
                  <p className="text-sm">{cliente.inscricao_municipal || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">E-mail NFe</label>
                  <p className="text-sm">{cliente.email_nfe || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">IBGE Município</label>
                  <p className="text-sm">{cliente.ibge_municipio || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Fora do Município</label>
                  <p className="text-sm">{cliente.fora_municipio ? 'Sim' : 'Não'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financeiro" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Financeiras</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Limite de Crédito</label>
                  <p className="text-sm font-semibold">
                    {cliente.limite_credito ? 
                      `R$ ${cliente.limite_credito.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 
                      '-'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">CNPJ Bancário</label>
                  <p className="text-sm">{cliente.cnpj_bancario || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Banco</label>
                  <p className="text-sm">{cliente.banco || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Agência</label>
                  <p className="text-sm">{cliente.agencia || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Conta</label>
                  <p className="text-sm">{cliente.conta || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">PIX</label>
                  <p className="text-sm">{cliente.pix || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="observacoes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Observações Gerais</label>
                {cliente.observacoes ? (
                  <div className="p-3 bg-muted rounded-md">
                    <p className="text-sm whitespace-pre-wrap">{cliente.observacoes}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Nenhuma observação cadastrada.</p>
                )}
              </div>
              
              <div className="mt-6 pt-4 border-t space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Informações do Sistema</label>
                <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium">Criado em:</span> {new Date(cliente.created_at).toLocaleString('pt-BR')}
                  </div>
                  <div>
                    <span className="font-medium">Atualizado em:</span> {new Date(cliente.updated_at).toLocaleString('pt-BR')}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mensagens" className="space-y-4">
          <MensagensTab tela="clientes" idRegistro={cliente.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}